import axios from 'axios';
import { refreshAccessToken } from './refreshToken';
import { getAccessTokenFromCookie, setAccessTokenToCookie } from './token';
import { fetchCsrfToken } from './csrf';

let accessToken = getAccessTokenFromCookie();

export const setAccessToken = (token) => {
  accessToken = token;
  console.log('🔐 [axiosInstance] setAccessToken 호출:', token);
  setAccessTokenToCookie(token);
};

// CSRF 토큰을 메모리에 저장
let csrfToken = null;

// CSRF 토큰을 설정하는 함수 (fetchCsrfToken에서 호출해야 함)
export const setCsrfToken = (token) => {
  csrfToken = token;
  console.log('🔒 [axiosInstance] CSRF 토큰 메모리 저장:', token);
};

// CSRF 토큰을 가져오는 함수
const getCsrfToken = () => {
  console.log(' [axiosInstance] 저장된 CSRF 토큰:', csrfToken);
  return csrfToken;
};

const api = axios.create({
  baseURL: "https://d-road.dromii.com",
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  // axios 자동 XSRF 처리 비활성화: 수동 헤더 설정이 쿠키값으로 덮어쓰이는 문제 방지
  xsrfCookieName: undefined,
  xsrfHeaderName: 'X-XSRF-TOKEN',
});

console.log(' [axiosInstance] Axios 인스턴스 초기화 완료');
console.log(' [axiosInstance] 초기 AccessToken:', accessToken);

api.interceptors.request.use(
  (config) => {
    console.log(' [axiosInstance] 요청 인터셉터 진입:', config.url);

    // axios 자동 XSRF 헤더 주입 비활성화 (어댑터에서 쿠키값으로 덮어쓰는 것 방지)
    config.xsrfCookieName = undefined;

    // 1) accessToken 설정
    if (
      accessToken &&
      config.url &&
      !config.url.endsWith('/api/refresh-token')
    ) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
      console.log(' [axiosInstance] Authorization 헤더 설정:', config.headers.Authorization);
    }

    // 2) CSRF 토큰 수동 설정 (API 응답에서 받은 토큰 사용)
    const currentCsrfToken = getCsrfToken();
    if (currentCsrfToken && !config.headers['X-XSRF-TOKEN']) {
      config.headers = config.headers || {};
      config.headers['X-XSRF-TOKEN'] = currentCsrfToken;
      console.log(' [axiosInstance] CSRF 토큰 헤더 설정:', currentCsrfToken);
      console.log('➡️ [axiosInstance] CSRF 토큰 헤더 설정:', currentCsrfToken);
    }
    
    // 2-1) 모든 환경에서 쿠키의 XSRF-TOKEN 값을 헤더에도 추가 (개발/프록시 환경 보완)
    try {
      if (typeof document !== 'undefined' && document.cookie) {
        const xsrfCookie = document.cookie
          .split('; ')
          .find((row) => row.startsWith('XSRF-TOKEN='))
          ?.split('=')[1];
        if (xsrfCookie) {
          config.headers = config.headers || {};
          config.headers['XSRF-TOKEN'] = xsrfCookie;
          console.log('🍪 [axiosInstance] 쿠키 XSRF-TOKEN을 요청 헤더에 추가:', xsrfCookie);
        } else {
          console.warn('🍪 [axiosInstance] 쿠키에 XSRF-TOKEN 없음');
        }
      }
    } catch (e) {
      console.warn('🍪 [axiosInstance] 쿠키 파싱 중 경고:', e);
    }

    // 3) 최종 확인
    if (config.headers['X-XSRF-TOKEN']) {
      console.log('✅ [axiosInstance] CSRF 토큰 헤더 확인됨:', config.headers['X-XSRF-TOKEN']);
    } else {
      console.warn('⚠️ [axiosInstance] CSRF 토큰 헤더 없음');
    }

    console.log('➡️ [axiosInstance] 최종 요청 헤더:', config.headers);
    return config;
  },
  (error) => {
    console.error('❌ [axiosInstance] 요청 인터셉터 에러:', error);
    return Promise.reject(error);
  }
);

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    error ? prom.reject(error) : prom.resolve(token);
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => {
    console.log('✅ [axiosInstance] 응답 수신:', response.config.url, response.status);
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    console.error('❌ [axiosInstance] 응답 에러 발생:', error.response?.status, originalRequest.url);

    // 401 에러 처리 (기존 로직 유지)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url.endsWith('/login') &&
      !originalRequest.url.endsWith('/api/refresh-token')
    ) {
      console.warn('🔁 [axiosInstance] 401 발생 → 리프레시 토큰 시도');

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token;
            // 저장된 CSRF 토큰도 다시 설정
            const currentCsrfToken = getCsrfToken();
            if (currentCsrfToken) {
              originalRequest.headers['X-XSRF-TOKEN'] = currentCsrfToken;
            }
            console.log('🔁 [axiosInstance] 대기 요청 재시도');
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const tokens = await refreshAccessToken();
        console.log('🔁 [axiosInstance] 리프레시 토큰 응답 데이터:', tokens);

        const newAccessToken = tokens?.data?.accessToken;

        if (!newAccessToken) {
          console.warn('❌ [axiosInstance] 새 accessToken 없음');
          setAccessToken(null);
          isRefreshing = false;
          processQueue(new Error('리프레시 토큰 없음'));
          return Promise.reject(error);
        }

        setAccessToken(newAccessToken);

        // CSRF 토큰 재요청
        await fetchCsrfToken();
        console.log('✅ [axiosInstance] CSRF 토큰 갱신 완료');

        processQueue(null, newAccessToken);
        originalRequest.headers.Authorization = 'Bearer ' + newAccessToken;
        
        // 새로운 CSRF 토큰도 설정 (fetchCsrfToken으로 갱신된 토큰)
        const newCsrfToken = getCsrfToken();
        if (newCsrfToken) {
          originalRequest.headers['X-XSRF-TOKEN'] = newCsrfToken;
        }
        
        isRefreshing = false;

        console.log('✅ [axiosInstance] 토큰 재설정 완료. 요청 재시도');
        return api(originalRequest);
      } catch (err) {
        console.error('❌ [axiosInstance] 리프레시 토큰 실패:', err);
        setAccessToken(null);
        isRefreshing = false;
        processQueue(err);
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  }
);

export default api;